import type { ReactNode } from 'react';
import ComparisonAccountModeBadge from './ComparisonAccountModeBadge';
import ComparisonAllocationOverlap from './ComparisonAllocationOverlap';
import ComparisonLoadingBanner from './ComparisonLoadingBanner';
import ComparisonPerformanceTable from './ComparisonPerformanceTable';
import ComparisonPositionsDiff from './ComparisonPositionsDiff';
import ComparisonRebalancingInfo from './ComparisonRebalancingInfo';
import ComparisonWinnersLosers from './ComparisonWinnersLosers';
import type { ComparisonAccount } from './comparisonTypes';

export interface PortfolioComparisonViewProps {
  accounts: ComparisonAccount[];
  /** Hide the built-in H2 when the parent renders PageNavHeader. */
  hideTitle?: boolean;
  /** Optional message rendered above the cards (e.g. "Loading 3 of 5"). */
  statusMessage?: string;
  /** Optional slot rendered in the top-right (e.g. close / share buttons). */
  actions?: ReactNode;
  /** When set and more than 2 accounts, chips show a remove control on hover. */
  onRemoveAccount?: (accountId: string) => void;
  /**
   * Slot for the overlay return chart. The shared module provides the data
   * builder (`buildNormalizedSeries`) but each frontend renders its own chart
   * with its locally-installed recharts (avoids bundling recharts in shared).
   */
  returnChart?: ReactNode;
  /**
   * Slot for the daily P&L overlay chart (day-over-day return deltas).
   * Rendered directly below `returnChart`. Same rationale as `returnChart`:
   * the frontend supplies the recharts-based component.
   */
  dailyPnlChart?: ReactNode;
}

const PortfolioComparisonView = ({
  accounts,
  hideTitle = false,
  statusMessage,
  actions,
  onRemoveAccount,
  returnChart,
  dailyPnlChart,
}: PortfolioComparisonViewProps) => {
  if (accounts.length < 2) {
    return (
      <div className="flex flex-col gap-4 text-foreground">
        <div className="rounded-[12px] border border-dashed border-white/[0.12] bg-[color:var(--nq-surface-card)] px-5 py-7 text-center">
          <h3 className="mb-1.5 font-semibold">
            Select 2 or more portfolios to compare
          </h3>
          <p className="text-white/55">
            Use the checkboxes above to pick the portfolios you want to compare
            side-by-side.
          </p>
        </div>
      </div>
    );
  }

  const errors = accounts
    .map((a) => (a.contentError ? `${a.label}: ${a.contentError}` : null))
    .filter((m): m is string => Boolean(m));
  const canRemoveFromComparison =
    Boolean(onRemoveAccount) && accounts.length > 2;

  return (
    <div className="flex flex-col gap-4 text-foreground">
      <ComparisonLoadingBanner accounts={accounts} />
      <header className="mt-0 flex flex-wrap items-start justify-between gap-4">
        <div>
          {!hideTitle && (
            <h2 className="mb-2 text-[1.35rem] font-semibold tracking-[0.01em]">
              Comparing {accounts.length} portfolios
            </h2>
          )}
          <div className="flex flex-wrap gap-2">
            {accounts.map((a) => (
              <span
                key={a.accountId}
                className={`inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.06] px-2.5 py-1 text-[13px] text-white/85${
                  canRemoveFromComparison
                    ? ' group transition-[background-color,border-color,padding] hover:border-white/[0.14] hover:bg-white/[0.09] hover:pr-1.5 focus-within:border-white/[0.14] focus-within:bg-white/[0.09] focus-within:pr-1.5'
                    : ''
                }`}
              >
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: a.color }}
                  aria-hidden
                />
                <span className="font-medium">{a.label}</span>
                <ComparisonAccountModeBadge account={a.account} />
                {canRemoveFromComparison && (
                  <span className="pointer-events-none ml-0 inline-flex max-w-0 items-center overflow-hidden opacity-0 transition-[max-width,margin,opacity] group-hover:pointer-events-auto group-hover:ml-0.5 group-hover:max-w-[22px] group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:ml-0.5 group-focus-within:max-w-[22px] group-focus-within:opacity-100">
                    <button
                      type="button"
                      className="inline-flex h-[18px] w-[18px] shrink-0 translate-x-1.5 cursor-pointer items-center justify-center rounded-full border-none bg-white/[0.08] p-0 text-[14px] leading-none text-white/55 transition-[transform,background-color,color] hover:bg-negative/28 hover:text-negative focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary/55 group-hover:translate-x-0 group-focus-within:translate-x-0"
                      onClick={() => onRemoveAccount!(a.accountId)}
                      aria-label={`Remove ${a.label} from comparison`}
                    >
                      ×
                    </button>
                  </span>
                )}
              </span>
            ))}
          </div>
          {statusMessage && (
            <p className="mt-2 text-[0.85rem] text-white/55">{statusMessage}</p>
          )}
          {errors.length > 0 && (
            <ul className="mt-2 list-disc pl-[18px] text-[0.85rem] text-[color:#ff8b8b]">
              {errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </header>

      <ComparisonPerformanceTable accounts={accounts} />

      {returnChart}

      {dailyPnlChart}

      <div className="grid grid-cols-1 gap-4 min-[960px]:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <ComparisonPositionsDiff accounts={accounts} />
        <ComparisonAllocationOverlap accounts={accounts} />
      </div>

      <ComparisonWinnersLosers accounts={accounts} />

      <ComparisonRebalancingInfo accounts={accounts} />
    </div>
  );
};

export default PortfolioComparisonView;
